/**
 * Application configuration
 * Uses environment variables set during build time
 */

// Get the API URL from environment variables
const getApiUrl = () => {
  // Production: Use environment variable
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Development: Use empty string (Vite proxy will handle it)
  if (import.meta.env.DEV) {
    return '';
  }

  // Fallback for production if not set
  console.warn('VITE_API_URL not set, using relative URLs');
  return '';
};

// Get the WebSocket URL from environment variables
const getWsUrl = () => {
  // Production: Use environment variable
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }

  // Development: Derive from current location (Vite proxy will handle it)
  if (import.meta.env.DEV) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }

  // Fallback for production if not set
  console.warn('VITE_WS_URL not set, using same host for WebSocket');
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
};

/**
 * Blazorly Agent Studio (separate app from Builder).
 * Dev default: blazorly-agent web (`vite --port 8004`).
 * Override with VITE_AGENT_APP_URL (no trailing slash).
 */
const getAgentAppUrl = () => {
  const fromEnv = (import.meta.env.VITE_AGENT_APP_URL || '').replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (import.meta.env.DEV) return 'http://localhost:8004';
  // Production default — override via wrangler/build env when host differs
  return 'https://agent.blazorly.com';
};

/**
 * Blazorly Crew (separate app from Builder).
 * Dev default: blazorly-crew web (`vite --port 8005`).
 * Override with VITE_CREW_APP_URL (no trailing slash).
 */
const getCrewAppUrl = () => {
  const fromEnv = (import.meta.env.VITE_CREW_APP_URL || '').replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (import.meta.env.DEV) return 'http://localhost:8005';
  return 'https://crew.blazorly.com';
};

/**
 * Platform SSO auth API — served by the dedicated identity worker
 * (blazorly-auth; dev localhost:9006, prod auth.blazorly.com). Builder
 * refreshes/me/logs out against it and shares the Domain=.blazorly.com
 * session cookie with agent and crew.
 */
const getAuthApiUrl = () => {
  const fromEnv = (import.meta.env.VITE_AUTH_API_URL || '').replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  // Dev: same-origin — the vite dev server proxies /auth to the identity
  // worker (localhost:9006) so the refresh cookie stays first-party.
  if (import.meta.env.DEV) return '';
  return 'https://auth.blazorly.com';
};

/**
 * The identity SPA (login/signup/password recovery UI).
 */
const getAuthAppUrl = () => {
  const fromEnv = (import.meta.env.VITE_AUTH_APP_URL || '').replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (import.meta.env.DEV) return 'http://localhost:8006';
  return 'https://auth.blazorly.com';
};

export const config = {
  // API base URL (e.g., "https://blazorly-backend-production.your-account.workers.dev" or "")
  apiUrl: getApiUrl(),

  // Platform auth surface base URL (login/refresh/me/logout)
  authApiUrl: getAuthApiUrl(),

  // Identity SPA origin (redirect target for sign-in)
  authAppUrl: getAuthAppUrl(),

  // WebSocket base URL (e.g., "wss://blazorly-backend-production.your-account.workers.dev")
  wsUrl: getWsUrl(),

  /** External Blazorly Agent Studio origin (Agents nav + redirects) */
  agentAppUrl: getAgentAppUrl(),

  /** External Blazorly Crew origin (product switcher) */
  crewAppUrl: getCrewAppUrl(),

  // Environment
  environment: import.meta.env.VITE_ENVIRONMENT || 'development',

  // Helper to check if running in production
  isProduction: import.meta.env.PROD,

  // Helper to check if running in development
  isDevelopment: import.meta.env.DEV,
} as const;

/**
 * Absolute Agent Studio URL for a deep path like `/agents/:id`. Kept separate
 * from `openAgentStudio` so anchor elements can link cross-origin directly
 * instead of routing through `window.open`.
 */
export function agentStudioHref(path = '/agents') {
  const base = config.agentAppUrl.replace(/\/$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
}

/** Open Agent Studio (agents list, or a deep path like `/agents/:id`). */
export function openAgentStudio(path = '/agents') {
  const url = agentStudioHref(path);
  window.open(url, '_blank', 'noopener,noreferrer');
  return url;
}
